import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Table, Typography, Button, Row, Col, Statistic, Divider } from 'antd';
import { ArrowLeftOutlined, FileProtectOutlined, DollarOutlined, DownloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { clientService } from '../../../common/services';
import Loader from '../../../components/loader/Loader';
import moment from 'moment';

const { Title, Text } = Typography;

export default function DetailContrat() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [contrat, setContrat] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadContrat();
    }, [id]);

    const loadContrat = async () => {
        try {
            const resp = await clientService.getContratDetail(id);
            if (resp.success) {
                setContrat(resp.data);
            }
        } catch (error) {
            toast.error('Erreur lors du chargement du contrat');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Loader />;
    if (!contrat) return null;

    const statutColors = { actif: 'green', expire: 'red', resilie: 'default', renouvele: 'blue' };

    const reglementColumns = [
        {
            title: 'Date',
            dataIndex: 'dateReglement',
            key: 'date',
            render: (d) => moment(d).format('DD/MM/YYYY'),
        },
        {
            title: 'Montant',
            dataIndex: 'montant',
            key: 'montant',
            render: (v) => `${(v || 0).toLocaleString()} FCFA`,
        },
        {
            title: 'Moyen',
            dataIndex: 'moyenPaiement',
            key: 'moyen',
            render: (m) => {
                const labels = { especes: 'Especes', cheque: 'Cheque', virement: 'Virement', mobile_money: 'Mobile Money' };
                return labels[m] || m;
            },
        },
        {
            title: 'Reference',
            dataIndex: 'reference',
            key: 'ref',
        },
    ];

    return (
        <div>
            <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/espace-client/contrats')}
                style={{ padding: 0, marginBottom: 16 }}
            >
                Retour a mes contrats
            </Button>

            <Title level={3}>
                <FileProtectOutlined style={{ marginRight: 8 }} />
                Contrat {contrat.numeroPolice}
                <Tag color={statutColors[contrat.statut]} style={{ marginLeft: 12 }}>{contrat.statut}</Tag>
            </Title>

            {/* Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={8}>
                    <Card>
                        <Statistic title="Prime TTC" value={contrat.primeTTC || 0} suffix="FCFA" valueStyle={{ fontSize: 18 }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card>
                        <Statistic title="Total verse" value={contrat.totalVerse || 0} suffix="FCFA" valueStyle={{ color: '#2e7d32', fontSize: 18 }} />
                    </Card>
                </Col>
                <Col xs={8}>
                    <Card>
                        <Statistic title="Reste a payer" value={contrat.resteAPayer || 0} suffix="FCFA" valueStyle={{ color: contrat.resteAPayer > 0 ? '#C8102E' : '#2e7d32', fontSize: 18 }} />
                    </Card>
                </Col>
            </Row>

            {/* Infos contrat */}
            <Card title="Informations du contrat" style={{ marginBottom: 24 }}>
                <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} bordered size="small">
                    <Descriptions.Item label="N. Police">{contrat.numeroPolice}</Descriptions.Item>
                    <Descriptions.Item label="N. Attestation">{contrat.numeroAttestation || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Compagnie">{contrat.compagnie?.nom}</Descriptions.Item>
                    <Descriptions.Item label="Type">{contrat.typeAssurance?.nom}</Descriptions.Item>
                    <Descriptions.Item label="Agence">{contrat.agence?.nom}</Descriptions.Item>
                    <Descriptions.Item label="Statut">
                        <Tag color={statutColors[contrat.statut]}>{contrat.statut}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Date effet">{moment(contrat.dateEffet).format('DD/MM/YYYY')}</Descriptions.Item>
                    <Descriptions.Item label="Date echeance">{moment(contrat.dateEcheance).format('DD/MM/YYYY')}</Descriptions.Item>
                    <Descriptions.Item label="Duree">{contrat.duree ? `${contrat.duree} mois` : '-'}</Descriptions.Item>
                    <Descriptions.Item label="Prime nette">{(contrat.primeNette || 0).toLocaleString()} FCFA</Descriptions.Item>
                    <Descriptions.Item label="Taxes">{(contrat.taxes || 0).toLocaleString()} FCFA</Descriptions.Item>
                    <Descriptions.Item label="Prime TTC">{(contrat.primeTTC || 0).toLocaleString()} FCFA</Descriptions.Item>
                </Descriptions>

                {contrat.immatriculation && (
                    <>
                        <Divider orientation="left">Vehicule</Divider>
                        <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
                            <Descriptions.Item label="Immatriculation">{contrat.immatriculation}</Descriptions.Item>
                            <Descriptions.Item label="Marque">{contrat.marqueVehicule || '-'}</Descriptions.Item>
                        </Descriptions>
                    </>
                )}
            </Card>

            {/* Reglements */}
            <Card title="Historique des paiements">
                <Table
                    dataSource={contrat.reglements || []}
                    columns={reglementColumns}
                    rowKey="_id"
                    pagination={false}
                    size="small"
                    scroll={{ x: 500 }}
                    locale={{ emptyText: 'Aucun paiement enregistre' }}
                />
            </Card>
        </div>
    );
}
